import classNames from 'classnames';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { KeyCode } from '../../types';
import IErrorObject from '../../types/ErrorObject';
import IFormProps from './FormProps';

const Form = <T extends {}>({
  onSubmit,
  data,
  render,
  onChange,
  className,
  allowMultipleSubmissions,
}: IFormProps<T>) => {
  const [formData, setFormData] = useState(data ? data : ({} as T));
  const [isDirty, setIsDirty] = useState(false);
  const [errors, setErrors] = useState<{ [field: string]: IErrorObject }>({});
  const isSubmitting = useRef<boolean>(false);

  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    if (data) {
      setFormData(data);
    } else {
      setFormData({} as T);
    }
  }, [data]);

  function handleSubmit() {
    setIsDirty(true);
    if (Object.keys(errors).length === 0) {
      if (!isSubmitting.current || allowMultipleSubmissions) {
        isSubmitting.current = true;
        onSubmit(formData);
        return true;
      }
      return false;
    } else {
      setTimeout(() => {
        const firstError = document.querySelector('.field-footer-error');
        if (firstError) {
          const closestError = firstError.closest('.form-field');
          if (closestError) {
            closestError.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }
      }, 0);
      return false;
    }
  }

  function handleFieldChange(fieldName: keyof T, value: T[keyof T]) {
    setData(fieldName, value);
  }

  function handleFieldError(name: keyof T & string, errorObj: IErrorObject) {
    const currentFieldError = errors[name];

    if (
      (!currentFieldError && !errorObj.hasError) ||
      (currentFieldError && errorObj.message === currentFieldError.message)
    ) {
      return;
    }

    setErrors((err) => {
      const newErrors = { ...err };
      if (errorObj.hasError) {
        newErrors[name] = errorObj;
      } else if (!errorObj.hasError && newErrors[name]) {
        delete newErrors[name];
      }

      return newErrors;
    });
  }

  function handleUnregister(fieldName: keyof T & string) {
    const datas = _.cloneDeep(formData);
    const errs = _.cloneDeep(errors);
    delete datas[fieldName];
    delete errors[fieldName];
    setFormData(datas);
    setErrors(errs);
  }

  function setData(fieldName: keyof T, value: T[keyof T]) {
    setFormData((previous) => {
      const newFormData = {
        ...previous,
        [fieldName]: value,
      };
      if (onChange) {
        onChange(newFormData);
      }
      return newFormData;
    });
  }

  function onKeyPress(event: React.KeyboardEvent) {
    if (event.keyCode === KeyCode.ENTER) {
      event.preventDefault();
      event.stopPropagation();

      handleSubmit();
    }
  }

  return (
    <form className={classNames('form', className)} onKeyDown={onKeyPress}>
      <>
        {render({
          state: { errors, data: formData, isDirty },
          updateError: handleFieldError,
          handleChange: handleFieldChange,
          handleSubmit,
          handleUnregister,
          setData,
          setFormData,
        })}
      </>
    </form>
  );
};

export default Form;
